import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Grid3x3, Save, Loader2 } from 'lucide-react';
import LandmarkPropertiesPanel from './minimap/LandmarkPropertiesPanel';

/**
 * 2D top-down draggable venue map editor.
 * X axis  = horizontal (east/west, −100..+100)
 * Z axis  = vertical   (north/south). We flip so positive Z appears at the top (north)
 *
 * Props:
 *  - booths, facilities, propItems, landmarks
 *  - onSaveBooth(booth), onSaveFacility(fac), onSaveProp(prop), onSaveLandmark(lm)
 *  - onCreateLandmark(type) — adds a new landmark at (0,0) with sensible defaults
 */
export default function MiniMapEditor({
  booths, facilities, propItems, landmarks = [],
  onSaveBooth, onSaveFacility, onSaveProp, onSaveLandmark, onCreateLandmark, onDeleteLandmark,
}) {
  // World bounds (must cover event + carpark + stadium areas)
  const WORLD = { xMin: -110, xMax: 110, zMin: -140, zMax: 110 };
  const worldW = WORLD.xMax - WORLD.xMin;
  const worldH = WORLD.zMax - WORLD.zMin;

  const [snap, setSnap] = useState(true);
  const [saving, setSaving] = useState(null);
  const [draft, setDraft] = useState({});
  const [dragging, setDragging] = useState(null);
  // Multi-select: Set of "kind-id" strings (e.g. "landmark-3", "booth-7")
  const [selection, setSelection] = useState(new Set());
  // Legacy single-landmark selection is now derived (first landmark in `selection`).
  const selectedLandmarkId = useMemo(() => {
    for (const key of selection) {
      const [kind, id] = key.split('-');
      if (kind === 'landmark') return Number(id);
    }
    return null;
  }, [selection]);
  const [resizing, setResizing] = useState(null);  // {id, corner, startW, startD, startClientX, startClientY}
  const [rotating, setRotating] = useState(null);  // {id, startAngle, startRot}
  const svgRef = useRef(null);

  // Undo/Redo history — snapshots the entire {booths, facilities, propItems, landmarks} state.
  // Each entry is a shallow map of "kind-id" -> partial fields we care about.
  const historyRef = useRef({ past: [], future: [] });
  const HISTORY_LIMIT = 20;
  const [, forceUpdate] = useState(0);
  const bump = useCallback(() => forceUpdate((n) => n + 1), []);

  const snapshotCurrent = useCallback(() => {
    const snap = { booths: {}, facilities: {}, propItems: {}, landmarks: {} };
    booths.forEach((b) => { snap.booths[b.id] = pickBooth(b); });
    facilities.forEach((f) => { snap.facilities[f.id] = pickFacility(f); });
    propItems.forEach((p) => { snap.propItems[p.id] = pickProp(p); });
    landmarks.forEach((l) => { snap.landmarks[l.id] = pickLandmark(l); });
    return snap;
  }, [booths, facilities, propItems, landmarks]);

  const pushHistory = useCallback((before) => {
    historyRef.current.past.push(before);
    if (historyRef.current.past.length > HISTORY_LIMIT) historyRef.current.past.shift();
    historyRef.current.future = []; // any new change wipes the redo stack
    bump();
  }, [bump]);

  const svgW = 900;
  const svgH = Math.round((worldH / worldW) * svgW);

  // world -> svg coordinate helpers.
  // Convert world coords (x, z) → SVG pixel coords (px, py).
  // The mini-map uses screen conventions: +X right by default, +Y down.
  // Three.js uses +X right and +/-Z for ground depth, so keep X unflipped and
  // flip only Z into SVG Y. This keeps left/right identical between mini-map
  // and the 3D top view.
  const w2sx = (x) => ((x - WORLD.xMin) / worldW) * svgW;
  const w2sy = (z) => ((WORLD.zMax - z) / worldH) * svgH;

  const eff = useCallback((kind, item) => {
    const key = `${kind}-${item.id}`;
    const d = draft[key];
    return {
      x: d ? Number(d.position_x) : Number(item.position_x || 0),
      z: d ? Number(d.position_z) : Number(item.position_z || 0),
    };
  }, [draft]);

  const clientToWorld = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, z: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, z: 0 };
    const local = pt.matrixTransform(ctm.inverse());
    const x = WORLD.xMin + (local.x / svgW) * worldW;
    const z = WORLD.zMax - (local.y / svgH) * worldH;
    return { x, z };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worldW, worldH]);

  const isSelected = useCallback((kind, id) => selection.has(`${kind}-${id}`), [selection]);

  const startDrag = (kind, item, e) => {
    e.preventDefault();
    e.stopPropagation();
    const world = clientToWorld(e);
    const key = `${kind}-${item.id}`;
    // Update selection: shift adds, plain click selects only this (unless already in selection)
    let effectiveSelection;
    setSelection((prev) => {
      const has = prev.has(key);
      if (e.shiftKey) {
        const next = new Set(prev);
        if (has) next.delete(key); else next.add(key);
        effectiveSelection = next;
        return next;
      }
      // Non-shift click: if already part of multi-selection, keep the group; else pick just this one.
      if (has && prev.size > 1) {
        effectiveSelection = prev;
        return prev;
      }
      const next = new Set([key]);
      effectiveSelection = next;
      return next;
    });
    // Snapshot origin positions for every selected item so we can bulk-move by delta.
    const originX = Number(item.position_x || 0);
    const originZ = Number(item.position_z || 0);
    // Build map of per-item origin positions
    const origins = {};
    // We can't rely on `effectiveSelection` here because setSelection is async; use a computed one.
    // Below: after this callback returns, we call requestAnimationFrame to capture. But simpler:
    // build the origin from the current selection PLUS the just-clicked item.
    const candidates = new Set(selection.has(key) && !e.shiftKey ? selection : (e.shiftKey ? (() => { const s = new Set(selection); if (s.has(key)) s.delete(key); else s.add(key); return s; })() : new Set([key])));
    candidates.add(key);
    candidates.forEach((k) => {
      const [ck, cid] = k.split('-');
      const source = ck === 'booth' ? booths : ck === 'facility' ? facilities : ck === 'prop' ? propItems : landmarks;
      const it = source.find((s) => s.id === Number(cid));
      if (it) origins[k] = { x: Number(it.position_x || 0), z: Number(it.position_z || 0) };
    });
    setDragging({
      kind, id: item.id,
      offX: world.x - originX,
      offZ: world.z - originZ,
      anchorX: originX,
      anchorZ: originZ,
      origins,
      before: snapshotCurrent(),
    });
  };

  const onMove = useCallback((e) => {
    if (!dragging) return;
    const world = clientToWorld(e);
    // Delta computed from anchor
    let dx = world.x - dragging.offX - dragging.anchorX;
    let dz = world.z - dragging.offZ - dragging.anchorZ;
    if (snap) {
      // Snap the delta so all items stay aligned
      dx = Math.round(dx / 5) * 5;
      dz = Math.round(dz / 5) * 5;
    }
    setDraft((prev) => {
      const next = { ...prev };
      Object.entries(dragging.origins).forEach(([key, orig]) => {
        next[key] = { position_x: orig.x + dx, position_z: orig.z + dz };
      });
      return next;
    });
  }, [dragging, snap, clientToWorld]);

  const endDrag = useCallback(async () => {
    if (!dragging) return;
    const drag = dragging;
    setDragging(null);
    const keys = Object.keys(drag.origins);
    const anyMoved = keys.some((k) => {
      const d = draft[k];
      if (!d) return false;
      return Number(d.position_x) !== drag.origins[k].x || Number(d.position_z) !== drag.origins[k].z;
    });
    if (!anyMoved) return;
    pushHistory(drag.before);
    setSaving('bulk');
    try {
      await Promise.all(keys.map(async (k) => {
        const d = draft[k];
        if (!d) return;
        const [ck, cid] = k.split('-');
        const source = ck === 'booth' ? booths : ck === 'facility' ? facilities : ck === 'prop' ? propItems : landmarks;
        const it = source.find((s) => s.id === Number(cid));
        if (!it) return;
        const updated = { ...it, position_x: d.position_x, position_z: d.position_z };
        if (ck === 'booth') await onSaveBooth(updated);
        else if (ck === 'facility') await onSaveFacility(updated);
        else if (ck === 'prop') await onSaveProp(updated);
        else if (ck === 'landmark') await onSaveLandmark(updated);
      }));
      setDraft((prev) => {
        const next = { ...prev };
        keys.forEach((k) => { delete next[k]; });
        return next;
      });
    } finally {
      setSaving(null);
    }
  }, [dragging, draft, booths, facilities, propItems, landmarks, onSaveBooth, onSaveFacility, onSaveProp, onSaveLandmark, pushHistory]);

  useEffect(() => {
    const move = (e) => onMove(e);
    const up = () => endDrag();
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [onMove, endDrag]);

  // Undo/Redo — restore a snapshot by diffing against current items
  const applySnapshot = useCallback(async (snap) => {
    setSaving('bulk');
    try {
      await Promise.all([
        ...booths.map(async (b) => {
          const prev = snap.booths[b.id];
          if (!prev) return;
          if (prev.position_x !== b.position_x || prev.position_z !== b.position_z || prev.rotation !== b.rotation) {
            await onSaveBooth({ ...b, ...prev });
          }
        }),
        ...facilities.map(async (f) => {
          const prev = snap.facilities[f.id];
          if (prev && (prev.position_x !== f.position_x || prev.position_z !== f.position_z)) {
            await onSaveFacility({ ...f, ...prev });
          }
        }),
        ...propItems.map(async (p) => {
          const prev = snap.propItems[p.id];
          if (prev && (prev.position_x !== p.position_x || prev.position_z !== p.position_z || prev.rotation !== p.rotation)) {
            await onSaveProp({ ...p, ...prev });
          }
        }),
        ...landmarks.map(async (l) => {
          const prev = snap.landmarks[l.id];
          if (!prev) return;
          const dirty = ['position_x', 'position_z', 'rotation', 'width', 'depth', 'height', 'scale', 'color', 'label']
            .some((k) => prev[k] !== l[k]);
          if (dirty) await onSaveLandmark({ ...l, ...prev });
        }),
      ]);
    } finally {
      setSaving(null);
    }
  }, [booths, facilities, propItems, landmarks, onSaveBooth, onSaveFacility, onSaveProp, onSaveLandmark]);

  const undo = useCallback(async () => {
    const h = historyRef.current;
    if (h.past.length === 0) return;
    const before = h.past.pop();
    h.future.push(snapshotCurrent());
    if (h.future.length > HISTORY_LIMIT) h.future.shift();
    bump();
    await applySnapshot(before);
  }, [applySnapshot, snapshotCurrent, bump]);

  const redo = useCallback(async () => {
    const h = historyRef.current;
    if (h.future.length === 0) return;
    const after = h.future.pop();
    h.past.push(snapshotCurrent());
    if (h.past.length > HISTORY_LIMIT) h.past.shift();
    bump();
    await applySnapshot(after);
  }, [applySnapshot, snapshotCurrent, bump]);

  // Global keys: Delete, Esc, Ctrl+Z, Ctrl+Shift+Z / Ctrl+Y
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault(); undo(); return;
      }
      if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'z') ||
          ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y')) {
        e.preventDefault(); redo(); return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedLandmarkId && onDeleteLandmark) {
        e.preventDefault();
        onDeleteLandmark(selectedLandmarkId);
        setSelection(new Set());
      }
      if (e.key === 'Escape') setSelection(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedLandmarkId, onDeleteLandmark, undo, redo]);

  // Resize handle drag (updates width & depth for rectangle landmarks)
  const startResize = (lm, corner, e) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing({
      id: lm.id,
      corner,
      startW: Number(lm.width || 20),
      startD: Number(lm.depth || 20),
      startX: e.clientX,
      startY: e.clientY,
      before: snapshotCurrent(),
    });
  };
  useEffect(() => {
    if (!resizing) return undefined;
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!ctm) return undefined;
    const scaleX = ctm.a; // svg-px per screen-px
    const scaleY = ctm.d;
    const worldPerSvgX = worldW / svgW;
    const worldPerSvgY = worldH / svgH;

    const onMoveResize = (e) => {
      const dxPx = (e.clientX - resizing.startX) / scaleX;
      const dyPx = (e.clientY - resizing.startY) / scaleY;
      // Mini-map is X-flipped (site-plan orientation) and Y is standard.
      // Convert screen pixel delta → world delta, then adjust sign for the flip.
      const dW = -dxPx * worldPerSvgX;   // X flipped → invert horizontal delta
      const dD = dyPx * worldPerSvgY;    // Y not flipped → direct
      // Direction based on corner (nw = subtract width from left, add depth from top, etc.)
      const signX = resizing.corner.includes('e') ? 1 : -1;
      const signZ = resizing.corner.includes('s') ? 1 : -1;
      let newW = Math.max(2, resizing.startW + signX * dW * 2);
      let newD = Math.max(2, resizing.startD + signZ * dD * 2);
      if (snap) {
        newW = Math.round(newW / 5) * 5 || 5;
        newD = Math.round(newD / 5) * 5 || 5;
      }
      setDraft((prev) => ({ ...prev, [`landmark-resize-${resizing.id}`]: { width: newW, depth: newD } }));
    };
    const onUpResize = async () => {
      const key = `landmark-resize-${resizing.id}`;
      const d = draft[key];
      const target = landmarks.find((l) => l.id === resizing.id);
      const before = resizing.before;
      setResizing(null);
      if (!d || !target) return;
      if (Number(d.width) === Number(target.width || 20) && Number(d.depth) === Number(target.depth || 20)) return;
      pushHistory(before);
      setSaving(`landmark-${target.id}`);
      try {
        await onSaveLandmark({ ...target, width: d.width, depth: d.depth });
      } finally {
        setSaving(null);
        setDraft((prev) => { const n = { ...prev }; delete n[key]; return n; });
      }
    };
    window.addEventListener('pointermove', onMoveResize);
    window.addEventListener('pointerup', onUpResize);
    return () => {
      window.removeEventListener('pointermove', onMoveResize);
      window.removeEventListener('pointerup', onUpResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizing, draft, landmarks, snap]);

  // Rotation handle drag — visually rotate rectangle landmarks (or point landmarks with rotation field).
  const startRotate = (lm, e) => {
    e.stopPropagation();
    e.preventDefault();
    const world = clientToWorld(e);
    const cx = Number(lm.position_x || 0);
    const cz = Number(lm.position_z || 0);
    const startAngle = Math.atan2(world.z - cz, world.x - cx) * (180 / Math.PI);
    setRotating({
      id: lm.id,
      startAngle,
      startRot: Number(lm.rotation || 0),
      before: snapshotCurrent(),
    });
  };
  useEffect(() => {
    if (!rotating) return undefined;
    const onMoveRotate = (e) => {
      const world = clientToWorld(e);
      const target = landmarks.find((l) => l.id === rotating.id);
      if (!target) return;
      const cx = Number(target.position_x || 0);
      const cz = Number(target.position_z || 0);
      const angle = Math.atan2(world.z - cz, world.x - cx) * (180 / Math.PI);
      let newRot = rotating.startRot + (rotating.startAngle - angle);
      newRot = ((newRot % 360) + 360) % 360;
      if (e.shiftKey) newRot = Math.round(newRot / 15) * 15;   // 15° snap while holding shift
      setDraft((prev) => ({ ...prev, [`landmark-rot-${rotating.id}`]: { rotation: newRot } }));
    };
    const onUpRotate = async () => {
      const key = `landmark-rot-${rotating.id}`;
      const d = draft[key];
      const target = landmarks.find((l) => l.id === rotating.id);
      const before = rotating.before;
      setRotating(null);
      if (!d || !target) return;
      if (Number(d.rotation) === Number(target.rotation || 0)) return;
      pushHistory(before);
      setSaving(`landmark-${target.id}`);
      try {
        await onSaveLandmark({ ...target, rotation: d.rotation });
      } finally {
        setSaving(null);
        setDraft((prev) => { const n = { ...prev }; delete n[key]; return n; });
      }
    };
    window.addEventListener('pointermove', onMoveRotate);
    window.addEventListener('pointerup', onUpRotate);
    return () => {
      window.removeEventListener('pointermove', onMoveRotate);
      window.removeEventListener('pointerup', onUpRotate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotating, draft, landmarks]);

  // Grid lines every 10 world units
  const gridLines = useMemo(() => {
    const lines = [];
    for (let x = Math.ceil(WORLD.xMin / 10) * 10; x <= WORLD.xMax; x += 10) {
      lines.push({ id: `vx${x}`, x1: w2sx(x), y1: 0, x2: w2sx(x), y2: svgH, major: x % 50 === 0 });
    }
    for (let z = Math.ceil(WORLD.zMin / 10) * 10; z <= WORLD.zMax; z += 10) {
      lines.push({ id: `hz${z}`, x1: 0, y1: w2sy(z), x2: svgW, y2: w2sy(z), major: z % 50 === 0 });
    }
    return lines;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgH]);

  return (
    <div className="space-y-3" data-testid="mini-map-editor">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-slate-300 text-xs">
          Drag booths, facilities, props or landmarks. <span className="text-slate-500">Shift-click to multi-select · Ctrl+Z / Ctrl+Y = undo / redo</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {selection.size > 1 && (
            <span className="text-[10px] uppercase font-black tracking-widest text-sky-300 bg-sky-500/20 border border-sky-500/40 rounded-full px-2 py-1" data-testid="selection-count">
              {selection.size} selected
            </span>
          )}
          <button
            onClick={undo}
            disabled={historyRef.current.past.length === 0}
            className="px-2.5 py-1.5 rounded-lg text-xs font-bold border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Undo (Ctrl+Z)"
            data-testid="minimap-undo-btn"
          >
            ↶ Undo
          </button>
          <button
            onClick={redo}
            disabled={historyRef.current.future.length === 0}
            className="px-2.5 py-1.5 rounded-lg text-xs font-bold border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Redo (Ctrl+Y / Ctrl+Shift+Z)"
            data-testid="minimap-redo-btn"
          >
            ↷ Redo
          </button>
          <button
            onClick={() => setSnap((s) => !s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 border transition-colors ${
              snap ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-slate-800 text-slate-400 border-slate-700'
            }`}
            data-testid="minimap-snap-toggle"
          >
            <Grid3x3 className="w-3.5 h-3.5" /> Snap 5m {snap ? 'ON' : 'OFF'}
          </button>
        </div>
        {(dragging || saving) && (
          <div className="text-xs text-emerald-400 flex items-center gap-1.5 w-full">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {dragging ? 'Dragging…' : saving ? 'Saving…' : ''}
          </div>
        )}
      </div>

      {/* Landmark create toolbar */}
      {onCreateLandmark && (
        <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-slate-900/60 border border-slate-800">
          <span className="text-[10px] uppercase font-black tracking-widest text-slate-400 mr-1">Add landmark:</span>
          {[
            { type: 'tree', label: '🌳 Tree' },
            { type: 'palm', label: '🌴 Palm' },
            { type: 'bush', label: '🌿 Bush' },
            { type: 'building', label: '🏢 Building' },
            { type: 'stadium', label: '🏟️ Stadium' },
            { type: 'carpark', label: '🅿️ Carpark' },
            { type: 'tree_line', label: '🌲 Tree Line' },
            { type: 'archway', label: '🚪 Entrance' },
          ].map(({ type, label }) => (
            <button
              key={type}
              onClick={() => onCreateLandmark(type)}
              className="px-2.5 py-1 rounded-md bg-slate-800 hover:bg-emerald-500/25 border border-slate-700 hover:border-emerald-500/50 text-white text-[11px] font-bold"
              data-testid={`create-landmark-${type}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="overflow-auto rounded-xl border border-slate-800 bg-slate-950 relative" style={{ maxHeight: '75vh' }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${svgW} ${svgH}`}
          width={svgW}
          height={svgH}
          className="block select-none touch-none"
          style={{ background: '#1a2e2a' }}
          onPointerDown={(e) => {
            // Click on the empty SVG background clears selection
            if (e.target === svgRef.current) setSelection(new Set());
          }}
        >
          {/* Grass / event area / carpark bands */}
          {/* Event area (z: -30 to +75) */}
          <rect x={w2sx(WORLD.xMin)} y={w2sy(75)} width={svgW} height={w2sy(-30) - w2sy(75)} fill="#334036" />
          {/* Tree line band (-30 to -45) */}
          <rect x={w2sx(WORLD.xMin)} y={w2sy(-30)} width={svgW} height={w2sy(-45) - w2sy(-30)} fill="#14532d" opacity="0.7" />
          {/* Carpark band (-50 to -120) */}
          <rect x={w2sx(WORLD.xMin)} y={w2sy(-50)} width={svgW} height={w2sy(-120) - w2sy(-50)} fill="#3a3a3a" />

          {/* Grid */}
          {gridLines.map((l) => (
            <line key={l.id} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke={l.major ? '#4b5563' : '#374151'} strokeWidth={l.major ? 0.8 : 0.4} />
          ))}
          {/* Zero axes */}
          <line x1={w2sx(0)} y1={0} x2={w2sx(0)} y2={svgH} stroke="#64748b" strokeDasharray="3,3" />
          <line x1={0} y1={w2sy(0)} x2={svgW} y2={w2sy(0)} stroke="#64748b" strokeDasharray="3,3" />

          {/* Zone labels */}
          <text x={12} y={16} fill="#94a3b8" fontSize="10" fontWeight="700">EVENT AREA (z: −30 to +75)</text>
          <text x={12} y={w2sy(-45) + 12} fill="#4ade80" fontSize="10" fontWeight="700">TREE LINE</text>
          <text x={12} y={w2sy(-85) + 12} fill="#94a3b8" fontSize="10" fontWeight="700">CARPARK</text>

          {/* Landmarks (stadium, carpark, tree_line, archway, palm, tree, bush, building) */}
          {landmarks.map((lm) => {
            const { x, z } = eff('landmark', lm);
            const cx = w2sx(x);
            const cy = w2sy(z);
            const isDragging = dragging && dragging.origins && dragging.origins[`landmark-${lm.id}`];
            const isSaving = saving === `landmark-${lm.id}` || saving === 'bulk';
            const isSel = isSelected('landmark', lm.id);
            const styleColor = lm.color || colorForLandmark(lm.type);
            // Live rotation from either the current rotate-drag or persisted value
            const rotDraft = draft[`landmark-rot-${lm.id}`];
            const rot = Number(rotDraft?.rotation ?? lm.rotation ?? 0);

            // Rectangle-shaped landmarks (drawn to real scale)
            if (['stadium', 'carpark', 'tree_line', 'building'].includes(lm.type)) {
              // Read live-resized dimensions from draft (or the current stored values)
              const resizeDraft = draft[`landmark-resize-${lm.id}`];
              const w = Number(resizeDraft?.width ?? lm.width ?? 20);
              const d = Number(resizeDraft?.depth ?? lm.depth ?? 20);
              const sw = (w / worldW) * svgW;
              const sh = (d / worldH) * svgH;
              const rx = lm.type === 'stadium' ? 20 : 2;
              const handleSize = 8;
              const strokeColor = isDragging ? '#fbbf24' : isSaving ? '#22c55e' : isSel ? '#38bdf8' : '#000';
              // Rotate SVG matches world rotation direction because the mini-map is
              // X-flipped (matches PDF orientation) and Y is standard.
              const svgRot = rot;
              // Rotation handle placed above (north of) the rectangle in world space.
              const handleOffset = sh / 2 + 20;
              return (
                <g
                  key={`lm${lm.id}`}
                  data-testid={`minimap-landmark-${lm.id}`}
                  transform={`rotate(${svgRot} ${cx} ${cy})`}
                >
                  <rect
                    x={cx - sw / 2}
                    y={cy - sh / 2}
                    width={sw}
                    height={sh}
                    fill={styleColor}
                    fillOpacity={lm.type === 'tree_line' ? 0.75 : 0.6}
                    stroke={strokeColor}
                    strokeWidth={isSel || isDragging ? 2 : 1}
                    rx={rx}
                    style={{ cursor: 'grab' }}
                    onPointerDown={(e) => startDrag('landmark', lm, e)}
                  />
                  <text x={cx} y={cy + 4} textAnchor="middle" fontSize={Math.min(11, sh / 2)} fill="#fff" fontWeight="800" pointerEvents="none" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}>
                    {lm.label || lm.type.replace('_', ' ').toUpperCase()}
                  </text>
                  {/* Resize handles - only when selected */}
                  {isSel && ['nw', 'ne', 'sw', 'se'].map((corner) => {
                    // Mini-map is X-flipped, so the 'e' (east) handle draws on the LEFT.
                    // Y is standard: 'n' (north) draws on top.
                    const hx = cx + (corner.includes('e') ? -sw / 2 : sw / 2) - handleSize / 2;
                    const hy = cy + (corner.includes('n') ? -sh / 2 : sh / 2) - handleSize / 2;
                    // Cursor diagonals swap because the mini-map is X-flipped
                    const cursor = (corner === 'ne' || corner === 'sw') ? 'nwse-resize' : 'nesw-resize';
                    return (
                      <rect
                        key={corner}
                        x={hx} y={hy}
                        width={handleSize} height={handleSize}
                        fill="#38bdf8" stroke="#fff" strokeWidth={1.5}
                        style={{ cursor }}
                        onPointerDown={(e) => startResize(lm, corner, e)}
                        data-testid={`resize-handle-${lm.id}-${corner}`}
                      />
                    );
                  })}
                  {/* Rotation handle - only when selected (small circle above the rect) */}
                  {isSel && (
                    <g>
                      <line x1={cx} y1={cy - sh / 2} x2={cx} y2={cy - handleOffset} stroke="#f59e0b" strokeWidth={1.2} strokeDasharray="3,2" />
                      <circle
                        cx={cx} cy={cy - handleOffset} r={6}
                        fill="#f59e0b" stroke="#fff" strokeWidth={1.5}
                        style={{ cursor: 'crosshair' }}
                        onPointerDown={(e) => startRotate(lm, e)}
                        data-testid={`rotate-handle-${lm.id}`}
                      />
                    </g>
                  )}
                </g>
              );
            }

            // Point-shaped landmarks (trees, palms, archway, bushes) — big circle
            const r = lm.type === 'archway' ? 8 : lm.type === 'palm' ? 5 : 4;
            return (
              <g
                key={`lm${lm.id}`}
                onPointerDown={(e) => startDrag('landmark', lm, e)}
                style={{ cursor: 'grab' }}
                data-testid={`minimap-landmark-${lm.id}`}
              >
                <circle
                  cx={cx} cy={cy} r={r + (isSel ? 2 : 0)}
                  fill={styleColor}
                  stroke={isDragging ? '#fbbf24' : isSaving ? '#22c55e' : isSel ? '#38bdf8' : '#000'}
                  strokeWidth={isSel || isDragging ? 2 : 0.8}
                />
                {lm.type === 'archway' && (
                  <text x={cx} y={cy - 10} textAnchor="middle" fontSize="9" fill="#fbbf24" fontWeight="800" pointerEvents="none">
                    {lm.label || 'ENTRANCE'}
                  </text>
                )}
              </g>
            );
          })}

          {/* Booths (rectangles) */}
          {booths.map((b) => {
            const { x, z } = eff('booth', b);
            const w = Number(b.width || 5);
            const d = Number(b.depth || 5);
            const cx = w2sx(x);
            const cy = w2sy(z);
            const sw = (w / worldW) * svgW;
            const sh = (d / worldH) * svgH;
            const isDragging = dragging && dragging.origins && dragging.origins[`booth-${b.id}`];
            const isSaving = saving === `booth-${b.id}` || saving === 'bulk';
            const isSel = isSelected('booth', b.id);
            return (
              <g
                key={`b${b.id}`}
                onPointerDown={(e) => startDrag('booth', b, e)}
                style={{ cursor: 'grab' }}
                data-testid={`minimap-booth-${b.id}`}
              >
                <rect
                  x={cx - sw / 2}
                  y={cy - sh / 2}
                  width={sw}
                  height={sh}
                  fill={b.color || '#3b82f6'}
                  stroke={isDragging ? '#fbbf24' : isSaving ? '#22c55e' : isSel ? '#38bdf8' : '#0f172a'}
                  strokeWidth={isSel || isDragging ? 2 : 0.6}
                  opacity={0.9}
                  rx={1}
                />
                {sw > 20 && (
                  <text x={cx} y={cy + 2.5} textAnchor="middle" fontSize={Math.min(9, sh - 2)} fill="#fff" fontWeight="700" pointerEvents="none">
                    {b.booth_number}
                  </text>
                )}
              </g>
            );
          })}

          {/* Facilities (circles with icon) */}
          {facilities.map((f) => {
            const { x, z } = eff('facility', f);
            const cx = w2sx(x);
            const cy = w2sy(z);
            const isDragging = dragging && dragging.origins && dragging.origins[`facility-${f.id}`];
            const isSaving = saving === `facility-${f.id}` || saving === 'bulk';
            const isSel = isSelected('facility', f.id);
            const color = colorForFacility(f.type);
            return (
              <g
                key={`f${f.id}`}
                onPointerDown={(e) => startDrag('facility', f, e)}
                style={{ cursor: 'grab' }}
                data-testid={`minimap-facility-${f.id}`}
              >
                <circle cx={cx} cy={cy} r={7} fill={color} stroke={isDragging ? '#fbbf24' : isSaving ? '#22c55e' : isSel ? '#38bdf8' : '#0f172a'} strokeWidth={isSel || isDragging ? 2 : 0.8} />
                <text x={cx} y={cy + 4} textAnchor="middle" fontSize="10" pointerEvents="none">{f.icon || '📍'}</text>
              </g>
            );
          })}

          {/* Props (diamonds/leaves) */}
          {propItems.map((p) => {
            const { x, z } = eff('prop', p);
            const cx = w2sx(x);
            const cy = w2sy(z);
            const isDragging = dragging && dragging.origins && dragging.origins[`prop-${p.id}`];
            const isSaving = saving === `prop-${p.id}` || saving === 'bulk';
            const isSel = isSelected('prop', p.id);
            const propColor = p.color || colorForProp(p.type);
            return (
              <g
                key={`p${p.id}`}
                onPointerDown={(e) => startDrag('prop', p, e)}
                style={{ cursor: 'grab' }}
                data-testid={`minimap-prop-${p.id}`}
              >
                <polygon
                  points={`${cx},${cy - 5} ${cx + 5},${cy} ${cx},${cy + 5} ${cx - 5},${cy}`}
                  fill={propColor}
                  stroke={isDragging ? '#fbbf24' : isSaving ? '#22c55e' : isSel ? '#38bdf8' : '#0f172a'}
                  strokeWidth={isSel || isDragging ? 2 : 0.6}
                />
              </g>
            );
          })}
        </svg>
      </div>

      {/* Selected-landmark properties panel */}
      {selectedLandmarkId && (
        <LandmarkPropertiesPanel
          landmark={landmarks.find((l) => l.id === selectedLandmarkId)}
          onSave={onSaveLandmark}
          onDelete={onDeleteLandmark}
          onDeselect={() => setSelection(new Set())}
        />
      )}

      <div className="text-[10px] text-slate-500 flex flex-wrap gap-3">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-500" /> Booth</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-cyan-500" /> Facility</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-green-500 rotate-45" /> Prop</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-slate-500" /> Landmark</span>
        <span className="ml-auto flex items-center gap-1"><Save className="w-3 h-3 text-emerald-400" /> Auto-saves on drag release</span>
      </div>
    </div>
  );
}

function colorForFacility(type) {
  const map = { toilet: '#06b6d4', prayer_room: '#16a34a', first_aid: '#dc2626', parking: '#1d4ed8', entrance: '#eab308', info: '#8b5cf6', food: '#f97316', shopping: '#ec4899', stadium: '#64748b' };
  return map[type] || '#8b5cf6';
}
function colorForProp(type) {
  const map = { tree: '#166534', bush: '#22c55e', palm: '#84cc16', lantern: '#fbbf24', bench: '#7c5e3c', flag: '#dc2626', sign: '#f59e0b', banner: '#22c55e', sculpture: '#a855f7', custom_icon: '#3b82f6' };
  return map[type] || '#94a3b8';
}
function colorForLandmark(type) {
  const map = { stadium: '#c0522d', carpark: '#3a3a3a', tree_line: '#166534', archway: '#eab308', palm: '#84cc16', tree: '#166534', bush: '#22c55e', building: '#94a3b8' };
  return map[type] || '#94a3b8';
}

// Snapshot pickers: capture only fields that undo/redo should restore.
function pickBooth(b) {
  return { position_x: Number(b.position_x || 0), position_z: Number(b.position_z || 0), rotation: Number(b.rotation || 0) };
}
function pickFacility(f) {
  return { position_x: Number(f.position_x || 0), position_z: Number(f.position_z || 0) };
}
function pickProp(p) {
  return { position_x: Number(p.position_x || 0), position_z: Number(p.position_z || 0), rotation: Number(p.rotation || 0) };
}
function pickLandmark(l) {
  return {
    position_x: Number(l.position_x || 0),
    position_z: Number(l.position_z || 0),
    rotation: Number(l.rotation || 0),
    width: l.width != null ? Number(l.width) : undefined,
    depth: l.depth != null ? Number(l.depth) : undefined,
    height: l.height != null ? Number(l.height) : undefined,
    scale: l.scale != null ? Number(l.scale) : undefined,
    color: l.color,
    label: l.label,
  };
}
